// Welcome Step Component
// First step of onboarding — introduces Revisr QHSE platform to newly approved users

import React from 'react';
import { FileText, ShieldCheck, FileEdit, BookOpen, ArrowRight, Sparkles } from 'lucide-react';
import { Card, CardContent } from '@foundation/components/ui/card';
import { Button } from '@foundation/components/ui/button';
import type { OnboardingStepProps } from '../OnboardingWizard';

type FeatureCard = {
  icon: React.ElementType;
  title: string;
  description: string;
  iconBg: string;
  iconColor: string;
};

const FEATURES: FeatureCard[] = [
  {
    icon: FileText,
    title: 'AI Document Management',
    description: 'Create, review and manage QHSE policies and procedures with AI assistance.',
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
  },
  {
    icon: ShieldCheck,
    title: 'Compliance Auditing',
    description: 'Run compliance checks against regulatory standards automatically.',
    iconBg: 'bg-green-50',
    iconColor: 'text-green-600',
  },
  {
    icon: FileEdit,
    title: 'Policy Generation',
    description: 'Generate policy drafts from templates using AI in seconds.',
    iconBg: 'bg-purple-50',
    iconColor: 'text-purple-600',
  },
  {
    icon: BookOpen,
    title: 'Knowledge Base',
    description: 'Centralise your organisation\'s documents and make them searchable.',
    iconBg: 'bg-orange-50',
    iconColor: 'text-orange-600',
  },
];

const WelcomeStep: React.FC<OnboardingStepProps> = ({ onNext }) => {
  return (
    <div className="flex flex-col items-center w-full max-w-2xl mx-auto space-y-8">

      {/* Hero Section */}
      <div className="text-center space-y-4">
        {/* Logo / Brand mark */}
        <div className="flex justify-center mb-2">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 shadow-sm">
            <Sparkles className="w-8 h-8 text-primary" />
          </div>
        </div>

        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            Welcome to Revisr
          </h1>
          <p className="text-lg text-muted-foreground max-w-md mx-auto">
            AI-powered QHSE Document Management & Compliance — built for teams who take safety seriously.
          </p>
        </div>
      </div>

      {/* Feature Highlights Grid */}
      <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-4">
        {FEATURES.map((feature) => {
          const Icon = feature.icon;
          return (
            <Card
              key={feature.title}
              className="border border-border/60 shadow-sm hover:shadow-md transition-shadow duration-200"
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className={`flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg ${feature.iconBg}`}>
                    <Icon className={`w-5 h-5 ${feature.iconColor}`} />
                  </div>
                  <div className="space-y-1 min-w-0">
                    <p className="font-semibold text-sm text-foreground leading-snug">
                      {feature.title}
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* CTA */}
      <div className="flex flex-col items-center gap-3 w-full">
        <Button
          size="lg"
          className="w-full sm:w-auto sm:px-12 gap-2 text-base font-semibold"
          onClick={() => onNext({})}
        >
          Get Started
          <ArrowRight className="w-4 h-4" />
        </Button>
        <p className="text-xs text-muted-foreground">
          Takes less than 2 minutes to set up your organisation
        </p>
      </div>

    </div>
  );
};

export { WelcomeStep };
export default WelcomeStep;
